import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useSubscription } from '../../../../context/subscription/SubscriptionContext';
import { subscriptionService } from '../../../../services/subscription/subscriptionService';
import { agentService } from '../../../../services/agent/agentService';
import MainLayout from '../../../../Layout/MainLayout';
import PageContainer from '../../../../components/layout/PageContainer';
import CheckoutHeader from './sections/CheckoutHeader';
import CheckoutPlanDetails from './sections/CheckoutPlanDetails';
import CheckoutBillingForm from './sections/CheckoutBillingForm';
import CheckoutGstSection from './sections/CheckoutGstSection';
import CheckoutOrderSummary from './sections/CheckoutOrderSummary';
import { CheckoutSuccessView, CheckoutCompletedView } from './sections/CheckoutModals';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2
  }).format(amount || 0);

const formatDate = (dateString) => {
  if (!dateString) return '—';
  try {
    return new Date(dateString).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
};

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planIdFromQuery = searchParams.get('plan');
  const intervalFromQuery = searchParams.get('interval') || 'month';
  const fromParam = searchParams.get('from');
  const modeParam = searchParams.get('mode'); // 'reactivate' or undefined
  const isCustomLinkCheckout = Boolean(searchParams.get('custom_link'));
  const isExternalCustomLink = isCustomLinkCheckout && fromParam !== 'profile' && fromParam !== 'subscription';

  const { subscription, refreshSubscription } = useSubscription();

  const [agentId, setAgentId] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [billingCycle, setBillingCycle] = useState(intervalFromQuery); // 'month' | 'year'
  const [loading, setLoading] = useState(true);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [customDeal, setCustomDeal] = useState(null);
  const [showPerkInfoModal, setShowPerkInfoModal] = useState(false);
  const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
  const [successPaymentData, setSuccessPaymentData] = useState(null);
  const [isAlreadyCompleted, setIsAlreadyCompleted] = useState(false);

  // Billing Details Form
  const [businessName, setBusinessName] = useState('');
  const [billingState, setBillingState] = useState('Maharashtra');
  const [pincode, setPincode] = useState('');
  const [billingEmail, setBillingEmail] = useState('');

  // GST State
  const [gstinNumber, setGstinNumber] = useState('');
  const [gstLegalName, setGstLegalName] = useState('');
  const [gstAddress, setGstAddress] = useState('');
  const [gstVerified, setGstVerified] = useState(false);

  // Smart Back Navigation
  const handleBackNavigation = () => {
    if (fromParam === 'profile') {
      navigate('/profile?tab=subscription', { replace: true });
    } else if (fromParam === 'subscription') {
      navigate('/subscription', { replace: true });
    } else {
      navigate('/subscription', { replace: true });
    }
  };

  // Fetch Available Plans & Agent Profile
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const customToken = searchParams.get('custom_link');
        let fetchedCustomDeal = null;

        if (customToken) {
          try {
            const statusRes = await subscriptionService.getCustomLinkStatus(customToken);
            if (statusRes?.is_completed) {
              setIsAlreadyCompleted(true);
            }
          } catch (stErr) {
            console.warn('Status check warning:', stErr.message);
          }

          try {
            const details = await subscriptionService.getCustomLinkDetails(customToken);
            if (details) {
              fetchedCustomDeal = {
                custom_price: details.custom_price,
                custom_voucher_limit: details.custom_voucher_limit,
                token: customToken,
                agent_name: details.agent_name
              };
              setCustomDeal(fetchedCustomDeal);
              if (details.billing_interval) setBillingCycle(details.billing_interval);
            }
          } catch (tokenErr) {
            toast.error(tokenErr.response?.data?.error || 'Invalid or expired payment link');
          }
        }

        const [plansRes, profileRes] = await Promise.all([
          subscriptionService.getAvailablePlans(),
          agentService.getProfile().catch(() => null)
        ]);

        const plans = plansRes?.plans || [];
        setAvailablePlans(plans);

        // Pre-select target plan
        if (planIdFromQuery) {
          const target = plans.find(p => p._id === planIdFromQuery || p.slug === planIdFromQuery);
          if (target) {
            setSelectedPlan(target);
          } else {
            setSelectedPlan(plans.find(p => p.slug === 'growth') || plans[0]);
          }
        } else {
          setSelectedPlan(plans.find(p => p.slug === 'growth') || plans[0]);
        }

        // Pre-fill profile billing & GST info
        if (profileRes?.data) {
          const agent = profileRes.data;
          setAgentId(agent._id);
          const nameVal = agent.agency_name || agent.company_name || agent.name || 'Orbitra Technologies LLP';
          setBusinessName(nameVal);
          setBillingEmail(agent.email || 'info@trrip.live');
          setPincode(agent.pincode || agent.zip_code || '401303');
          setBillingState(agent.state || 'Maharashtra');

          const existingGst = agent.gstin || agent.gst_number || '';
          if (existingGst) {
            setGstinNumber(existingGst);
            setGstLegalName(agent.gst_legal_name || agent.business_name || nameVal);
            setGstAddress(agent.gst_address || agent.address || '');
            if (existingGst.trim().length === 15) {
              setGstVerified(true);
            }
          }
        }
      } catch (err) {
        toast.error('Failed to load checkout data.');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [planIdFromQuery, searchParams]);

  const handleGstNumberChange = (val) => {
    const clean = val.toUpperCase().trim();
    setGstinNumber(clean);
    if (clean.length === 15) {
      setGstVerified(true);
    } else {
      setGstVerified(false);
    }
  };

  const saveGstDetailsToProfile = async () => {
    if (!agentId || !gstinNumber.trim()) return;
    try {
      await agentService.updateProfile(agentId, {
        gstin: gstinNumber.trim().toUpperCase(),
        gst_legal_name: gstLegalName.trim() || businessName,
        gst_address: gstAddress.trim()
      });
      console.log('✅ Saved GSTIN details to agent profile from checkout');
    } catch (err) {
      console.warn('⚠️ Non-blocking GST profile update error:', err.message);
    }
  };

  // Pricing calculations
  const customPriceFromQuery = searchParams.get('custom_price') ? Number(searchParams.get('custom_price')) : null;
  const customVoucherFromQuery = searchParams.get('custom_voucher_limit') ? Number(searchParams.get('custom_voucher_limit')) : null;

  const effectiveCustomPrice = customDeal?.custom_price || customPriceFromQuery || null;
  const effectiveCustomVoucherLimit = customDeal?.custom_voucher_limit || customVoucherFromQuery || null;
  const customLinkToken = customDeal?.token || searchParams.get('custom_link') || null;

  const currentPlanObj = useMemo(() => {
    if (!subscription?.plan_id) return null;
    if (typeof subscription.plan_id === 'object' && subscription.plan_id.slug) {
      return subscription.plan_id;
    }
    const targetId = typeof subscription.plan_id === 'object' ? subscription.plan_id._id : subscription.plan_id;
    return availablePlans.find(p => String(p._id) === String(targetId)) || null;
  }, [subscription, availablePlans]);

  const slugRanks = { starter: 1, pro: 2, growth: 3, enterprise: 4 };
  const currentPlanSlug = currentPlanObj?.slug || (typeof subscription?.plan_id === 'object' ? subscription?.plan_id?.slug : '') || '';
  const currentPlanRank = currentPlanSlug ? (slugRanks[currentPlanSlug] || 1) : 0;
  const selectedPlanRank = slugRanks[selectedPlan?.slug] || 1;

  const pendingChange = subscription?.pending_change;
  const isApprovedPendingChange = Boolean(
    pendingChange &&
    (pendingChange.status === 'mandate_pending' || pendingChange.status === 'approved')
  );

  const approvedTargetInterval = pendingChange?.target_billing_interval || intervalFromQuery || 'month';
  const approvedTargetPlanId = pendingChange?.target_plan_id?._id || pendingChange?.target_plan_id;

  // When there is an approved pending plan change, strictly lock selectedPlan to the approved target plan
  useEffect(() => {
    if (isApprovedPendingChange && approvedTargetPlanId && availablePlans.length > 0) {
      const target = availablePlans.find(p => String(p._id) === String(approvedTargetPlanId) || p.slug === String(approvedTargetPlanId));
      if (target && (!selectedPlan || String(selectedPlan._id) !== String(target._id))) {
        setSelectedPlan(target);
      }
    }
  }, [isApprovedPendingChange, approvedTargetPlanId, availablePlans, selectedPlan]);

  const isCustomDealCheckout = Boolean(effectiveCustomPrice);
  const isCurrentAnnualSubscriber = !isCustomDealCheckout &&
    !isApprovedPendingChange &&
    subscription?.status === 'active' &&
    subscription?.billing_interval === 'year';

  const isAlreadyOnCurrentMonthlyPlan = !isCustomDealCheckout &&
    !isApprovedPendingChange &&
    subscription?.status === 'active' &&
    subscription?.billing_interval === 'month' &&
    selectedPlan &&
    (currentPlanSlug === selectedPlan.slug || String(currentPlanObj?._id) === String(selectedPlan._id));

  const isDowngradeToMonthly = !isCustomDealCheckout &&
    !isApprovedPendingChange &&
    subscription?.status === 'active' &&
    subscription?.billing_interval === 'month' &&
    currentPlanRank > 0 &&
    selectedPlanRank < currentPlanRank;

  useEffect(() => {
    if (isApprovedPendingChange) {
      setBillingCycle(approvedTargetInterval === 'year' ? 'year' : 'month');
    } else if (isCustomDealCheckout) {
      setBillingCycle('month');
    } else if (intervalFromQuery) {
      setBillingCycle(intervalFromQuery);
    } else if (isCurrentAnnualSubscriber || isAlreadyOnCurrentMonthlyPlan || isDowngradeToMonthly) {
      setBillingCycle('year');
    }
  }, [isApprovedPendingChange, approvedTargetInterval, isCustomDealCheckout, intervalFromQuery, isCurrentAnnualSubscriber, isAlreadyOnCurrentMonthlyPlan, isDowngradeToMonthly]);

  // Realtime status polling for custom link checkouts
  useEffect(() => {
    const token = customDeal?.token || searchParams.get('custom_link');
    if (!token || isPaymentSuccess || isAlreadyCompleted) return;
    const interval = setInterval(async () => {
      try {
        const res = await subscriptionService.getCustomLinkStatus(token);
        if (res?.is_completed) {
          setIsAlreadyCompleted(true);
        }
      } catch (err) {
        // silent catch
      }
    }, 4000);
    return () => clearInterval(interval);
  }, [customDeal, searchParams, isPaymentSuccess, isAlreadyCompleted]);

  // Auto-redirect logged-in portal users to /subscription when payment is completed
  useEffect(() => {
    if (!isExternalCustomLink && (isAlreadyCompleted || isPaymentSuccess)) {
      toast.success('Mandate Authorized! Your plan is active.');
      navigate('/subscription', { replace: true });
    }
  }, [isExternalCustomLink, isAlreadyCompleted, isPaymentSuccess, navigate]);

  const isAnnual = billingCycle === 'year';
  const baseMonthlyPrice = effectiveCustomPrice || selectedPlan?.monthly_price || 3499;
  const baseAnnualPrice = effectiveCustomPrice
    ? effectiveCustomPrice
    : (selectedPlan?.yearly_price || selectedPlan?.annual_price || Math.round(baseMonthlyPrice * 12 * 0.8));

  const basePrice = isAnnual ? baseAnnualPrice : baseMonthlyPrice;

  const subtotal = basePrice;

  // 18% GST Calculation (No GSTIN -> CGST+SGST, GSTIN starts with 27 -> CGST+SGST, Other GSTIN -> IGST)
  const cleanGstin = (gstinNumber || '').trim();
  const isSameState = !cleanGstin || cleanGstin.startsWith('27');
  const totalGstAmount = subtotal * 0.18;
  const cgstAmount = totalGstAmount / 2;
  const sgstAmount = totalGstAmount / 2;
  const totalPlanAmount = subtotal + totalGstAmount;

  const isCurrentAnnual = subscription?.billing_interval === 'year' || subscription?.pending_change?.target_billing_interval === 'year';
  const isTrialUser = subscription?.status === 'trial' && (subscription?.trial_end || subscription?.trialEnd);

  const isDowngrade = (subscription?.status === 'active' || subscription?.status === 'cancelled') &&
    subscription?.plan_type === 'paid' &&
    selectedPlanRank < currentPlanRank;

  const isMidCycleSubscriber = (subscription?.status === 'active' || subscription?.status === 'cancelled') &&
    subscription?.plan_type === 'paid' &&
    !isDowngrade &&
    !isCurrentAnnual;

  const isReactivating = (modeParam === 'reactivate' || (subscription?.status === 'cancelled' && subscription?.plan_id?.slug === selectedPlan?.slug)) && !isDowngrade && !isCurrentAnnual;

  const isMandateOnly = (isTrialUser || isMidCycleSubscriber || isReactivating) && !effectiveCustomPrice && !isCurrentAnnual;

  const cycleEndDate = subscription?.status === 'trial'
    ? (subscription?.trial_end || subscription?.trialEnd)
    : (subscription?.end_date || subscription?.next_billing_at || subscription?.nextBillingAt);
  const formattedCycleEnd = cycleEndDate ? formatDate(cycleEndDate) : 'your next cycle date';
  const activeDaysRemaining = cycleEndDate
    ? Math.max(1, Math.ceil((new Date(cycleEndDate) - new Date()) / (1000 * 60 * 60 * 24)))
    : 30;

  const amountDueToday = isMandateOnly ? 5 : totalPlanAmount;

  // Handle Razorpay Payment / Mandate Trigger
  const handleProceedToPayment = async () => {
    if (!selectedPlan) return;
    if (isDowngradeToMonthly && billingCycle === 'month' && !isApprovedPendingChange) {
      toast.error('Downgrading to a lower monthly plan requires a request to Trrip support.');
      return;
    }
    setIsProcessingPayment(true);

    if (gstinNumber.trim()) {
      await saveGstDetailsToProfile();
    }

    const gstPayload = gstinNumber.trim() ? {
      gstin: gstinNumber.trim().toUpperCase(),
      gst_legal_name: gstLegalName.trim() || businessName,
      gst_address: gstAddress.trim()
    } : {};

    const customPayload = {
      ...(customLinkToken ? { custom_token: customLinkToken } : {}),
      ...(effectiveCustomPrice ? { custom_price: effectiveCustomPrice, custom_voucher_limit: effectiveCustomVoucherLimit } : {})
    };

    try {
      const checkoutData = await subscriptionService.createCheckout(selectedPlan._id, billingCycle, {
        is_mandate_only: isMandateOnly,
        amount_due_today: amountDueToday,
        ...customPayload,
        ...gstPayload
      });

      // Save pending checkout session for auto-recovery if user closes popup early
      if (checkoutData?.subscription_id) {
        const pendingData = {
          subscription_id: checkoutData.subscription_id,
          plan_id: selectedPlan._id,
          billing_interval: billingCycle,
          timestamp: Date.now()
        };
        localStorage.setItem('trrip_pending_checkout', JSON.stringify(pendingData));
        sessionStorage.setItem('trrip_pending_checkout', JSON.stringify(pendingData));
      }

      const options = {
        key: checkoutData.razorpay_key || checkoutData.key,
        subscription_id: checkoutData.subscription_id,
        name: 'Trrip',
        description: `${selectedPlan.name} Plan Subscription`,
        image: 'https://trrip.in/logo.png',
        prefill: {
          name: businessName || '',
          email: billingEmail || ''
        },
        theme: {
          color: '#067978'
        },
        handler: async (response) => {
          try {
            await subscriptionService.verifyPayment({
              razorpay_subscription_id: response.razorpay_subscription_id || checkoutData.subscription_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              plan_id: selectedPlan._id,
              billing_interval: billingCycle,
              ...customPayload,
              ...gstPayload
            });
            localStorage.removeItem('trrip_pending_checkout');
            sessionStorage.removeItem('trrip_pending_checkout');
            await refreshSubscription();
            
            setSuccessPaymentData({
              subscription_id: response.razorpay_subscription_id || checkoutData.subscription_id,
              payment_id: response.razorpay_payment_id,
              plan_name: selectedPlan?.name,
              amount: totalPlanAmount
            });
            setIsPaymentSuccess(true);
          } catch (verifyError) {
            toast.error('Payment mandate verification failed. Please try again.');
          } finally {
            setIsProcessingPayment(false);
          }
        },
        modal: {
          ondismiss: async () => {
            setIsProcessingPayment(false);
            try {
              if (checkoutData?.subscription_id) {
                const res = await subscriptionService.checkPendingCheckout({
                  subscription_id: checkoutData.subscription_id,
                  plan_id: selectedPlan._id,
                  billing_interval: billingCycle
                });
                if (res.verified) {
                  localStorage.removeItem('trrip_pending_checkout');
                  sessionStorage.removeItem('trrip_pending_checkout');
                  toast.success(`Plan updated successfully! You are now on ${selectedPlan.name} Tier.`);
                  await refreshSubscription();
                  handleBackNavigation();
                }
              }
            } catch (checkErr) {
              // silent fail on dismiss
            }
          }
        }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message || 'Failed to initialize payment mandate');
      setIsProcessingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center font-sans">
        <div className="w-9 h-9 border-3 border-[#067978] border-t-transparent rounded-full animate-spin" />
        <p className="mt-3 text-xs font-bold text-slate-500">Loading checkout details...</p>
      </div>
    );
  }

  if (isPaymentSuccess) {
    const successContent = (
      <CheckoutSuccessView
        successPaymentData={successPaymentData}
        selectedPlan={selectedPlan}
        effectiveCustomVoucherLimit={effectiveCustomVoucherLimit}
        totalPlanAmount={totalPlanAmount}
        formatCurrency={formatCurrency}
        navigate={navigate}
      />
    );
    return isCustomLinkCheckout ? successContent : <MainLayout><PageContainer>{successContent}</PageContainer></MainLayout>;
  }

  if (isAlreadyCompleted) {
    const completedContent = (
      <CheckoutCompletedView navigate={navigate} />
    );
    return isCustomLinkCheckout ? completedContent : <MainLayout><PageContainer>{completedContent}</PageContainer></MainLayout>;
  }

  const checkoutContent = (
    <div className="space-y-6">
      {/* Title Subheader */}
      <CheckoutHeader
        isCustomLinkCheckout={isCustomLinkCheckout}
        handleBackNavigation={handleBackNavigation}
        isReactivating={isReactivating}
        selectedPlan={selectedPlan}
      />

      {/* Main Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6 items-start">
        {/* Left Column - Steps */}
        <div className="lg:col-span-7 xl:col-span-8 space-y-4 sm:space-y-5">
          <CheckoutPlanDetails
            effectiveCustomPrice={effectiveCustomPrice}
            effectiveCustomVoucherLimit={effectiveCustomVoucherLimit}
            isCurrentAnnualSubscriber={isCurrentAnnualSubscriber}
            isAlreadyOnCurrentMonthlyPlan={isAlreadyOnCurrentMonthlyPlan}
            isDowngradeToMonthly={isDowngradeToMonthly}
            isApprovedPendingChange={isApprovedPendingChange}
            approvedTargetInterval={approvedTargetInterval}
            billingCycle={billingCycle}
            setBillingCycle={setBillingCycle}
            baseMonthlyPrice={baseMonthlyPrice}
            baseAnnualPrice={baseAnnualPrice}
            isCustomDealCheckout={isCustomDealCheckout}
            selectedPlan={selectedPlan}
            formatCurrency={formatCurrency}
            navigate={navigate}
          />

          <CheckoutBillingForm
            isExternalCustomLink={isExternalCustomLink}
            businessName={businessName}
            setBusinessName={setBusinessName}
            customDeal={customDeal}
            billingEmail={billingEmail}
            setBillingEmail={setBillingEmail}
            gstinNumber={gstinNumber}
            billingState={billingState}
            setBillingState={setBillingState}
            pincode={pincode}
            setPincode={setPincode}
          />

          {!isExternalCustomLink && (
            <CheckoutGstSection
              gstinNumber={gstinNumber}
              handleGstNumberChange={handleGstNumberChange}
              saveGstDetailsToProfile={saveGstDetailsToProfile}
              gstVerified={gstVerified}
              gstLegalName={gstLegalName}
              setGstLegalName={setGstLegalName}
              gstAddress={gstAddress}
              setGstAddress={setGstAddress}
              isSameState={isSameState}
              billingState={billingState}
              cgstAmount={cgstAmount}
              sgstAmount={sgstAmount}
              totalGstAmount={totalGstAmount}
              formatCurrency={formatCurrency}
            />
          )}
        </div>

        {/* Right Column - Order Summary Card */}
        <CheckoutOrderSummary
          selectedPlan={selectedPlan}
          isCustomDealCheckout={isCustomDealCheckout}
          billingCycle={billingCycle}
          isMandateOnly={isMandateOnly}
          isTrialUser={isTrialUser}
          isReactivating={isReactivating}
          formattedCycleEnd={formattedCycleEnd}
          activeDaysRemaining={activeDaysRemaining}
          showPerkInfoModal={showPerkInfoModal}
          setShowPerkInfoModal={setShowPerkInfoModal}
          totalPlanAmount={totalPlanAmount}
          basePrice={basePrice}
          appliedCoupon={null}
          discountAmount={0}
          subtotal={subtotal}
          isSameState={isSameState}
          cgstAmount={cgstAmount}
          sgstAmount={sgstAmount}
          totalGstAmount={totalGstAmount}
          amountDueToday={amountDueToday}
          handleProceedToPayment={handleProceedToPayment}
          isProcessingPayment={isProcessingPayment}
          formatCurrency={formatCurrency}
        />
      </div>
    </div>
  );

  if (isCustomLinkCheckout) {
    return (
      <div className="min-h-screen bg-slate-50 font-sans text-slate-800 py-6 sm:py-10">
        <main className="max-w-6xl mx-auto px-4 sm:px-6">
          {checkoutContent}
        </main>
      </div>
    );
  }

  return (
    <MainLayout activeTab="dashboard">
      <PageContainer className="font-sans text-slate-800">
        {checkoutContent}
      </PageContainer>
    </MainLayout>
  );
};

export default CheckoutPage;
